import { useTranslation } from "react-i18next";
import { CalendarDays, X } from "lucide-react";
import { Button, Field, FieldLabel, IconButton, Input, SegmentedTabs, Typography } from "@akuna/ui";
import { DateFieldPopover } from "@/components/ui/date-field-popover";
import { nextOccurrences, type ActivityFrequency, type ActivityRecurrence } from "./activitiesModel";

type Mode = "once" | ActivityFrequency;
const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

const fromDateInput = (v: string | null | undefined) => (v ? new Date(`${v}T12:00:00`) : undefined);
const toDateInput = (d: Date | undefined) => {
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const modeOf = (value: ActivityRecurrence | null): Mode => (value ? value.frequency : "once");

const defaultsFor = (mode: Mode, anchor: string): ActivityRecurrence | null => {
  if (mode === "once") return null;
  const base: ActivityRecurrence = { frequency: mode, intervalDays: null, daysOfWeek: [], daysOfMonth: [], endsOn: null };
  if (mode === "weekly") return { ...base, daysOfWeek: [new Date(`${anchor}T12:00:00`).getDay()] };
  if (mode === "every_n_days") return { ...base, intervalDays: 2 };
  if (mode === "monthly") return { ...base, daysOfMonth: [new Date(`${anchor}T12:00:00`).getDate()] };
  return base;
};

export function ActivityRecurrenceField({
  value, scheduledDate, onChange,
}: { value: ActivityRecurrence | null; scheduledDate: string; onChange: (next: ActivityRecurrence | null) => void }) {
  const { t, i18n } = useTranslation("daycareHotel");
  const locale = i18n.language.startsWith("pt") ? "pt-BR" : "en-US";
  const mode = modeOf(value);

  const patch = (next: Partial<ActivityRecurrence>) => {
    if (!value) return;
    onChange({ ...value, ...next });
  };
  const toggleDay = (d: number) =>
    patch({ daysOfWeek: value?.daysOfWeek.includes(d) ? value.daysOfWeek.filter((x) => x !== d) : [...(value?.daysOfWeek ?? []), d].sort((a, b) => a - b) });

  const options: Array<{ mode: Mode; label: string }> = [
    { mode: "once", label: t("activities.recOnce") },
    { mode: "daily", label: t("activities.recDaily") },
    { mode: "weekly", label: t("activities.recWeekly") },
    { mode: "monthly", label: t("activities.recMonthly") },
    // Escape hatch: só aparece enquanto o valor salvo ainda for every_n_days.
    ...(mode === "every_n_days" ? [{ mode: "every_n_days" as Mode, label: t("activities.recEveryN") }] : []),
  ];

  // Busca uma a mais que o exibido para saber se a série continua depois da última data mostrada.
  const upcoming = value ? nextOccurrences(scheduledDate, value, 6) : [];
  const previewDates = upcoming
    .slice(0, 5)
    .map((iso) => new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" }).format(new Date(`${iso}T12:00:00`)));
  const previewText = previewDates.length
    ? t("nextDatesInline", { dates: previewDates.join(" · ") }) + (upcoming.length > 5 ? " …" : "")
    : null;

  return (
    <Field>
      <FieldLabel>{t("activities.recurrenceLabel")}</FieldLabel>
      <SegmentedTabs
        label={t("activities.recurrenceLabel")}
        items={options.map((o) => ({ value: o.mode, label: o.label }))}
        onValueChange={(next) => onChange(defaultsFor(next as Mode, scheduledDate))}
        scrollable="mobile"
        value={mode}
        variant="outline-secondary"
      />

      {value && (
        <div className="mt-2 grid gap-3 rounded-xl border border-border bg-muted/30 p-3">
          {value.frequency === "weekly" && (
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(d)}
                  className={`h-8 w-8 rounded-full border text-xs font-semibold transition-colors ${
                    value.daysOfWeek.includes(d) ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground"
                  }`}
                >
                  {(t("weekdayShort", { returnObjects: true }) as string[])[d]}
                </button>
              ))}
            </div>
          )}

          {value.frequency === "every_n_days" && (
            <Field>
              <FieldLabel>{t("activities.intervalDays")}</FieldLabel>
              <Input type="number" min={1} value={value.intervalDays ?? ""} onChange={(e) => patch({ intervalDays: Number(e.target.value) || null })} />
            </Field>
          )}

          <div className="flex flex-wrap items-center gap-x-1 gap-y-1.5">
            <DateFieldPopover
              selected={fromDateInput(value.endsOn)}
              onSelect={(date) => patch({ endsOn: toDateInput(date) })}
              trigger={
                <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs text-muted-foreground">
                  <CalendarDays size={14} />
                  {value.endsOn
                    ? t("untilDate", {
                        date: new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(
                          fromDateInput(value.endsOn),
                        ),
                      })
                    : t("noEndDate")}
                </Button>
              }
            />
            {value.endsOn && (
              <IconButton
                type="button"
                label={t("clearEndDate")}
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-muted-foreground"
                onClick={() => patch({ endsOn: null })}
              >
                <X size={14} />
              </IconButton>
            )}
          </div>

          {previewText && (
            <Typography asChild variant="caption">
              <span className="text-muted-foreground">{previewText}</span>
            </Typography>
          )}
        </div>
      )}
    </Field>
  );
}
